import { Centrifuge, UnauthorizedError } from 'centrifuge'

import { setConnectionState, registerRetryHandler } from './Composables/useRealtimeConnectionState'
import {
  getToken,
  isRealtimeTransportDisabled,
  RealtimeTransportDisabledError,
  useRealtimeToken,
} from './Composables/useRealtimeToken'
import type { TokenResponse } from './types'

let instance: Centrifuge | null = null
let connecting: Promise<Centrifuge | null> | null = null
let warnedTokenFailure = false

async function buildClient(): Promise<Centrifuge | null> {
  let initial: TokenResponse
  try {
    initial = await getToken()
  } catch (err: unknown) {
    if (err instanceof RealtimeTransportDisabledError || isRealtimeTransportDisabled()) {
      console.info('[realtime] transport unavailable — live updates disabled for this session')
      return null
    }

    setConnectionState('failed')
    if (!warnedTokenFailure) {
      warnedTokenFailure = true
      console.warn('[realtime] token request failed — live updates unavailable', err)
    }
    return null
  }

  const client = new Centrifuge(initial.url, {
    token: initial.token,
    getToken: async () => {
      try {
        const fresh = await useRealtimeToken().refresh()
        return fresh.token
      } catch (err: unknown) {
        if (err instanceof RealtimeTransportDisabledError) {
          // UnauthorizedError tells centrifuge-js to stop reconnecting.
          throw new UnauthorizedError('realtime transport unavailable')
        }
        throw err
      }
    },
  })

  client.on('connected', () => setConnectionState('connected'))
  client.on('connecting', () => setConnectionState('reconnecting'))
  client.on('disconnected', (ctx) => {
    if (ctx.code >= 3500 && ctx.code <= 3503) {
      setConnectionState('reconnecting')
      return
    }
    setConnectionState('failed')
  })
  client.on('error', () => setConnectionState('degraded'))

  registerRetryHandler(() => {
    client.disconnect()
    void client.connect()
  })

  client.connect()
  return client
}

/**
 * Resolves null (never rejects) when the realtime transport is unavailable —
 * consumers must degrade to HTTP-only behavior in that case.
 */
export async function getRealtimeClient(): Promise<Centrifuge | null> {
  if (instance !== null) return instance
  if (isRealtimeTransportDisabled()) return null
  if (connecting !== null) return connecting

  connecting = buildClient()
    .then((client) => {
      instance = client
      return client
    })
    .finally(() => {
      connecting = null
    })

  return connecting
}

export function resetRealtimeClient(): void {
  instance?.disconnect()
  instance = null
}
